/**
 * TourContext — Section-based guided tour state.
 * Supports picking individual sections, replaying sections,
 * and a full sequential "play all" mode.
 * Persists completed sections across the session.
 */
import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { TOUR_SECTIONS, flattenSteps, resolveShowcaseFlow } from '../components/tour/tourSteps';

const STORAGE_KEY = 'terramind_tour_completed';
const FAB_DISMISSED_KEY = 'terramind_tour_fab_dismissed';

function loadCompletedSections() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch (e) { /* storage unavailable */ }
  return new Set();
}

function saveFabDismissed(val) {
  try { localStorage.setItem(FAB_DISMISSED_KEY, val ? '1' : '0'); } catch (e) { /* storage unavailable */ }
}

function loadFabDismissed() {
  try { return localStorage.getItem(FAB_DISMISSED_KEY) === '1'; } catch (e) { /* storage unavailable */ }
  return false;
}

const TourContext = createContext();

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within TourProvider');
  return ctx;
};

export const TourProvider = ({ children }) => {
  // Core state
  const [tourActive, setTourActive] = useState(false);        // overlay visible at all
  const [showSelector, setShowSelector] = useState(false);     // section picker card visible
  const [activeSection, setActiveSection] = useState(null);    // which section is playing (id string)
  const [sectionStep, setSectionStep] = useState(0);           // current step within active section
  const [completedSections, setCompletedSections] = useState(() => loadCompletedSections());
  const [fabDismissed, setFabDismissed] = useState(() => loadFabDismissed());
  const [playAllMode, setPlayAllMode] = useState(false);       // sequential full tour
  // Showcase mode: the tour advances on its own instead of waiting for a
  // click. This is what makes the real app recordable as a demo video — the
  // same steps, the same real screens, nobody driving. Anyone can take over at
  // any point, which stops the timer rather than fighting it.
  const [autoPlay, setAutoPlay] = useState(false);
  // Where we are in the curated demo. -1 means the demo is not running, and
  // the tour behaves exactly as it always has.
  const [flowPos, setFlowPos] = useState(-1);
  const navigateRef = useRef(null);

  // The demo's running order: a short path through the real screens rather
  // than every tutorial step in feature order. Resolved once — it only
  // changes when the step definitions do.
  const showcaseFlow = useMemo(() => resolveShowcaseFlow(), []);

  // Precompute flat steps
  const allSteps = useMemo(() => flattenSteps(), []);
  const totalSteps = allSteps.length;

  // Derive global tourStep index for overlay positioning
  const tourStep = useMemo(() => {
    if (!activeSection) return 0;
    const sectionObj = TOUR_SECTIONS.find((s) => s.id === activeSection);
    if (!sectionObj) return 0;
    let offset = 0;
    for (const sec of TOUR_SECTIONS) {
      if (sec.id === activeSection) break;
      offset += sec.steps.length;
    }
    return offset + sectionStep;
  }, [activeSection, sectionStep]);

  // Get current section object
  const currentSection = useMemo(
    () => TOUR_SECTIONS.find((s) => s.id === activeSection) || null,
    [activeSection],
  );

  // Get current step object (with section metadata)
  const currentStep = useMemo(() => {
    if (!currentSection) return null;
    const step = currentSection.steps[sectionStep];
    if (!step) return null;
    return {
      ...step,
      sectionId: currentSection.id,
      sectionColor: currentSection.color,
      sectionIcon: currentSection.icon,
      sectionTitleKey: currentSection.titleKey,
      sectionTitleFallback: currentSection.titleFallback,
      sectionStepCount: currentSection.steps.length,
      stepInSection: sectionStep,
    };
  }, [currentSection, sectionStep]);

  // ── Start the tour (shows section selector) ──────────────────────────
  const startTour = useCallback(() => {
    setTourActive(true);
    setShowSelector(true);
    setActiveSection(null);
    setSectionStep(0);
    setPlayAllMode(false);
  }, []);

  // Alias for backward compat
  const startFullTour = useCallback(() => {
    setTourActive(true);
    setShowSelector(false);
    setPlayAllMode(true);
    // Start with the first section
    const first = TOUR_SECTIONS[0];
    if (first) {
      setActiveSection(first.id);
      setSectionStep(0);
    }
  }, []);

  // ── Showcase: the whole tour, playing itself ─────────────────────────
  // Used by /showcase for the stand and for recording. Identical to the full
  // tour a farmer can run by hand — same sections, same real screens — except
  // that it advances on a timer.
  const startShowcase = useCallback(() => {
    if (showcaseFlow.length === 0) return;
    // Arm the reload guard. A trade-stand browser resets flowPos to -1 on any
    // refresh, and the /showcase launcher only runs when that URL is the one
    // loaded — it replaces itself with '/' the moment the trailer starts, so a
    // stray F5 mid-demo would land on the raw app instead. This flag, set here
    // and cleared only when someone exits (closeTour), re-arms the trailer on
    // the next mount. sessionStorage is cleared when the tab closes, so it
    // never leaks the trailer into a later, unrelated session.
    try { sessionStorage.setItem('terramind_showcase_armed', '1'); } catch (e) { /* private mode */ }
    setTourActive(true);
    setShowSelector(false);
    setPlayAllMode(false);
    setAutoPlay(true);
    setFlowPos(0);
    setActiveSection(showcaseFlow[0].sectionId);
    setSectionStep(showcaseFlow[0].stepInSection);
  }, [showcaseFlow]);

  // Re-arm the trailer after a reload. Runs only when the demo is not already
  // playing and the armed flag is present, so it fires once on a refreshed
  // stand and never fights the running loop (starting it sets flowPos >= 0,
  // which short-circuits the next pass). Cleared exits (Escape, close, an
  // off-script redirect) all route through closeTour, which drops the flag.
  useEffect(() => {
    if (flowPos >= 0 || showcaseFlow.length === 0) return;
    let armed = false;
    try { armed = sessionStorage.getItem('terramind_showcase_armed') === '1'; } catch (e) { /* private mode */ }
    if (armed) startShowcase();
  }, [flowPos, showcaseFlow, startShowcase]);

  // Walk the curated flow. Loops: a stand screen that stops on its last slide
  // shows nothing for the rest of the afternoon.
  const goToFlowPos = useCallback((pos) => {
    if (showcaseFlow.length === 0) return;
    const wrapped = ((pos % showcaseFlow.length) + showcaseFlow.length) % showcaseFlow.length;
    const entry = showcaseFlow[wrapped];
    setFlowPos(wrapped);
    setActiveSection(entry.sectionId);
    setSectionStep(entry.stepInSection);
  }, [showcaseFlow]);

  // Taking control stops the narration where it stands rather than snapping
  // back to the beginning: whoever just reached for the screen wants to look
  // at what is on it.
  const takeControl = useCallback(() => setAutoPlay(false), []);
  const resumeShowcase = useCallback(() => setAutoPlay(true), []);
  const inShowcase = flowPos >= 0;

  // ── Start a specific section ─────────────────────────────────────────
  const startSection = useCallback((sectionId) => {
    const section = TOUR_SECTIONS.find((s) => s.id === sectionId);
    if (!section) return;
    setShowSelector(false);
    setActiveSection(sectionId);
    setSectionStep(0);
  }, []);

  // ── Exit current section back to selector ────────────────────────────
  const exitSection = useCallback(() => {
    setActiveSection(null);
    setSectionStep(0);
    setShowSelector(true);
    setPlayAllMode(false);
  }, []);

  // Persist completed sections whenever they change
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...completedSections])); } catch (e) { /* storage unavailable */ }
  }, [completedSections]);

  // Whether all sections have been seen at least once
  const allSectionsCompleted = useMemo(
    () => TOUR_SECTIONS.every((s) => completedSections.has(s.id)),
    [completedSections],
  );

  // User manually hides the FAB ("Mark all as read")
  const dismissFab = useCallback(() => {
    setFabDismissed(true);
    saveFabDismissed(true);
  }, []);

  // Reset tour (show FAB again)
  const resetTour = useCallback(() => {
    setCompletedSections(new Set());
    setFabDismissed(false);
    saveFabDismissed(false);
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* storage unavailable */ }
  }, []);

  // ── Close tour entirely ──────────────────────────────────────────────
  const closeTour = useCallback(() => {
    // Disarm the reload guard first, so the mount effect cannot re-arm the
    // trailer that the person is deliberately leaving. This is the single exit
    // path — the Escape guard, the close button, and the off-script redirect
    // check all call it.
    try { sessionStorage.removeItem('terramind_showcase_armed'); } catch (e) { /* private mode */ }
    setTourActive(false);
    setShowSelector(false);
    setActiveSection(null);
    setSectionStep(0);
    setPlayAllMode(false);
    setAutoPlay(false);
    setFlowPos(-1);
  }, []);

  // ── Next step (within section, or advance to next section in play-all) ─
  const nextStep = useCallback(() => {
    if (flowPos >= 0) { goToFlowPos(flowPos + 1); return; }
    if (!currentSection) return;

    const maxStep = currentSection.steps.length - 1;
    if (sectionStep < maxStep) {
      setSectionStep((prev) => prev + 1);
      return;
    }

    // Section completed
    setCompletedSections((prev) => new Set([...prev, currentSection.id]));

    if (playAllMode) {
      // Find next section
      const currentIdx = TOUR_SECTIONS.findIndex((s) => s.id === currentSection.id);
      if (currentIdx < TOUR_SECTIONS.length - 1) {
        const nextSection = TOUR_SECTIONS[currentIdx + 1];
        setActiveSection(nextSection.id);
        setSectionStep(0);
      } else if (autoPlay) {
        // Showcase mode loops. A stand screen that stops on its last slide is
        // a stand screen showing nothing for the rest of the afternoon.
        setActiveSection(TOUR_SECTIONS[0].id);
        setSectionStep(0);
      } else {
        // All sections done
        closeTour();
      }
    } else {
      // Single section mode — back to selector
      exitSection();
    }
  }, [currentSection, sectionStep, playAllMode, autoPlay, closeTour, exitSection,
      flowPos, goToFlowPos]);

  // ── Previous step ────────────────────────────────────────────────────
  const prevStep = useCallback(() => {
    if (flowPos >= 0) { goToFlowPos(flowPos - 1); return; }
    if (sectionStep > 0) {
      setSectionStep((prev) => prev - 1);
    }
  }, [sectionStep, flowPos, goToFlowPos]);

  // ── Go to specific global step (backward compat) ─────────────────────
  const goToStep = useCallback((globalIdx) => {
    if (globalIdx < 0 || globalIdx >= totalSteps) return;
    const step = allSteps[globalIdx];
    if (!step) return;
    setActiveSection(step.sectionId);
    setSectionStep(step.stepInSection);
    setShowSelector(false);
  }, [allSteps, totalSteps]);

  // Allow TourOverlay to register the navigate function
  const registerNavigate = useCallback((nav) => {
    navigateRef.current = nav;
  }, []);

  const value = {
    // State
    tourActive,
    tourStep,
    totalSteps,
    showSelector,
    activeSection,
    sectionStep,
    completedSections,
    playAllMode,
    autoPlay,
    inShowcase,
    flowPos,
    showcaseLength: showcaseFlow.length,
    currentSection,
    currentStep,
    allSectionsCompleted,
    fabDismissed,

    // Actions
    startTour,
    startFullTour,
    startShowcase,
    goToFlowPos,
    takeControl,
    resumeShowcase,
    startSection,
    exitSection,
    closeTour,
    nextStep,
    prevStep,
    goToStep,
    dismissFab,
    resetTour,

    // Navigation ref
    navigateRef,
    registerNavigate,

    // Backward compat (unused but keeps DemoTour working)
    setTotalSteps: () => {},
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export default TourContext;
